<template>
    <div class="login-form w-100 max-w-400-px">
        <div v-if="isGoogleSignInInitialized">
            <GoogleAuthButton
                ref="googleAuthButton"
                @verifiedSuccess="handleLoginResponse"
            ></GoogleAuthButton>
            <div class="divider my-3s text-md">
                <hr
                    :data-content="$t('common.or')"
                    class="divider-content border-mercury my-2s"
                />
            </div>
        </div>
        <b-alert v-if="remaining" show variant="warning">
            <div class="d-flex align-items-center">
                <i class="fas fa-exclamation-triangle"></i>
                <div class="pl-3">
                    {{
                        $t('auth.remaining_warning_message', {
                            remaining,
                            blockedTime,
                        })
                    }}
                </div>
            </div>
        </b-alert>
        <b-alert v-if="accountLocked" show variant="danger">
            <div class="d-flex align-items-center">
                <i class="fas fa-exclamation-triangle"></i>
                <div class="pl-3">
                    <p class="font-weight-bold mb-0">
                        {{ $t('auth.temporarily_blocked') }}
                    </p>
                    {{
                        $t('auth.account_locked', {
                            blockedTime,
                        })
                    }}
                </div>
            </div>
        </b-alert>
        <AccountBannedAlert v-if="accountBanned"></AccountBannedAlert>
        <b-form @submit.prevent="submit">
            <b-form-group
                :label="$t('auth.username')"
                label-class="font-weight-600 text-grey text-semi-xl pb-2s"
            >
                <b-form-input
                    v-model="form.username"
                    :formatter="removeSpace"
                    :tabindex="1"
                    class="bg-catskill-white border-0 h-48-px"
                    size="lg"
                    type="text"
                ></b-form-input>
                <b-form-invalid-feedback
                    :state="!!error.username ? false : null"
                >
                    {{ error.username }}
                </b-form-invalid-feedback>
            </b-form-group>
            <b-form-group
                label-class="font-weight-600 text-grey text-semi-xl pb-2s"
            >
                <template #label>
                    <div
                        class="
                            d-flex
                            align-items-center
                            justify-content-between
                        "
                    >
                        {{ $t('auth.password') }}
                        <span
                            class="
                                text-cinnabar
                                cursor-pointer
                                font-weight-400
                                text-md
                                pl-3
                            "
                            @click="redirectForgotPassword"
                        >
                            {{ $t('auth.forgot_password') }}
                        </span>
                    </div>
                </template>
                <b-input-group>
                    <b-form-input
                        v-model="form.password"
                        :formatter="removeSpace"
                        :tabindex="2"
                        :type="showPassword ? 'text' : 'password'"
                        class="bg-catskill-white border-0 h-48-px"
                        size="lg"
                    ></b-form-input>
                    <template #append>
                        <b-button
                            class="text-grey bg-hover-catskill-white border-0"
                            variant="catskill-white"
                            @click="showPassword = !showPassword"
                        >
                            <i
                                :class="
                                    !showPassword ? 'fa-eye-slash' : 'fa-eye'
                                "
                                class="far text-grey font-weight-600 text-xl"
                            ></i>
                        </b-button>
                    </template>
                </b-input-group>
                <b-form-invalid-feedback
                    :state="!!error.password ? false : null"
                >
                    {{ error.password }}
                </b-form-invalid-feedback>
            </b-form-group>
            <div
                v-if="captchaVisible"
                class="d-flex justify-content-center mb-3"
            >
                <VueRecaptcha
                    :loadRecaptchaScript="true"
                    :sitekey="recaptchaSiteKey"
                    @error="resetCaptchaToken"
                    @expired="resetCaptchaToken"
                    @verify="captchaSuccess"
                ></VueRecaptcha>
            </div>
            <b-button
                ref="btnSubmit"
                :disabled="!canSubmit || loading"
                :tabindex="3"
                block
                class="text-white font-weight-600 h-40-px mb-3s"
                type="submit"
                variant="cinnabar"
            >
                <i v-if="loading" class="far fa-spinner fa-spin mr-2"></i>
                {{ loading ? $t('common.processing') : $t('button.login') }}
            </b-button>
        </b-form>
        <div class="text-blue-haze text-center text-md">
            {{ $t('auth.dont_have_account') }}
            <span
                class="text-cinnabar cursor-pointer"
                @click="redirectRegister"
                >{{ $t('auth.create_new_account') }}</span
            >
        </div>
    </div>
</template>

<script>
import AccountBannedAlert from '@/components/auth/AccountBannedAlert/AccountBannedAlert';
import GoogleAuthButton from '@/components/auth/GoogleAuthButton/GoogleAuthButton';
import { ERRORS } from '@/constants/error';
import { HTTP_STATUS } from '@/constants/http-status';
import errorMixin from '@/mixins/error';
import VueRecaptcha from 'vue-recaptcha';
import { mapState } from 'vuex';

export default {
    name: 'LoginForm',
    components: { GoogleAuthButton, AccountBannedAlert, VueRecaptcha },
    mixins: [errorMixin],
    data() {
        return {
            form: {
                username: '',
                password: '',
            },
            error: {
                username: '',
                password: '',
            },
            loading: false,
            showPassword: false,
            recaptchaSiteKey: process.env.VUE_APP_CAPTCHA_SITE_KEY,
            captchaToken: '',
            remaining: 0,
            accountLocked: false,
            blockedTime: 0,
            accountBanned: false,
            captchaVisible: false,
        };
    },
    computed: {
        ...mapState('guest', ['isGoogleSignInInitialized']),
        submitStatus() {
            return !this.error.username && !this.error.password;
        },
        canSubmit() {
            return (
                !this.captchaVisible ||
                (this.captchaVisible && this.captchaToken)
            );
        },
    },
    methods: {
        captchaSuccess(token) {
            this.captchaToken = token;
        },
        resetCaptchaToken() {
            this.captchaToken = '';
        },
        removeSpace(string) {
            return string.replace(/\s/g, '');
        },
        redirectForgotPassword() {
            this.$helpers.redirectStore('/forgot-password');
        },
        redirectRegister() {
            this.$helpers.redirectStore('/?register=true');
        },
        setEmptyError() {
            this.remaining = 0;
            this.accountLocked = false;
            this.accountBanned = false;
            this.error = {
                username: null,
                password: null,
            };
        },
        validateForm() {
            this.setEmptyError();

            const rules = {
                username: ['required'],
                password: ['required'],
            };

            const messages = {
                'username.required': this.$t('error.REQUIRED_USERNAME'),
                'password.required': this.$t('error.REQUIRED_PASSWORD'),
            };

            const validateErrors = this.$helpers.validateForm(
                this.$helpers.trimForm(this.form),
                rules,
                messages
            );

            if (
                this.$helpers.isObject(validateErrors) &&
                !this.$helpers.isEmptyObj(validateErrors)
            ) {
                Object.keys(this.error).forEach((i) => {
                    if (validateErrors[i]) {
                        this.error[i] = validateErrors[i];
                    }
                });
                return false;
            }
            return true;
        },
        submit() {
            this.$refs.googleAuthButton?.reset();
            if (!this.validateForm()) {
                return;
            }
            if (!this.canSubmit) {
                return;
            }
            if (this.submitStatus) {
                this.loading = true;
                this.$store
                    .dispatch('auth/login', this.form)
                    .then(({ data }) => {
                        this.handleLoginResponse(data);
                    })
                    .catch((e) => {
                        const status = e?.response?.status || 0;
                        const data = e?.response?.data || {};
                        if (
                            status === HTTP_STATUS.FORBIDDEN &&
                            data.messageCode === ERRORS.ACCOUNT_BANNED
                        ) {
                            this.accountBanned = true;
                            return;
                        }
                        this.captchaVisible = false;
                        setTimeout(() => {
                            this.captchaVisible = true;
                        }, 200);
                        this.resetCaptchaToken();

                        if (
                            status === HTTP_STATUS.UNAUTHORIZED &&
                            data.messageCode ===
                                ERRORS.USERNAME_PASSWORD_INCORRECT
                        ) {
                            this.error.password = this.$t(
                                'error.' + ERRORS.USERNAME_PASSWORD_INCORRECT
                            );
                            this.remaining = +data?.remaining || 0;
                            this.blockedTime = +data?.blockedTime || 0;
                            return;
                        }
                        if (
                            status === HTTP_STATUS.UNAUTHORIZED &&
                            data.messageCode === ERRORS.ACCOUNT_LOCKED
                        ) {
                            this.remaining = 0;
                            this.blockedTime = +data?.blockedTime || 0;
                            this.accountLocked = true;
                            return;
                        }
                        this.handleThrowError(e);
                    })
                    .finally(() => {
                        this.loading = false;
                    });
            }
        },
        handleLoginResponse(data) {
            this.$emit('onLogin', data);
        },
    },
};
</script>

<style lang="scss" scoped>
@import 'LoginForm';
</style>
